import * as t from 'io-ts';

import {
  ContributingSegmentsOptionsEnum,
  ReportFilterOptionsEnum,
} from '../../analytics/helpers/analyticsReportHelpers';
import {AnalyticsReportFilterOptions} from './analyticsSavedViewModel';
import {makeApiType} from './apiTypes';
import {parseConversation, RConversation} from './conversationModel';
import {parseTags, RTag} from './tagModel';

/**
 * Analytics models and parsers for metric details.
 */

/*
 * Runtime type.
 */

export const RAnalyticsDetailsConversationDirty = t.intersection([
  RConversation,
  t.type({
    tags: t.readonlyArray(RTag),
  }),
]);

export const RAnalyticsDetailsConversationsDirty = t.type({
  items: t.readonlyArray(RAnalyticsDetailsConversationDirty),
  nonAccessibleConversationIds: t.readonlyArray(t.number),
  metadata: t.type({
    totalConversations: t.number,
    totalSegments: t.number,
    isFinal: t.boolean,
  }),
});

export const RContributingSegmentsOptionsBooleanDirty = t.literal(true);
export const RContributingSegmentsOptionsSubMetricDirty = t.type({
  [ContributingSegmentsOptionsEnum.SUB_METRIC]: t.string,
});
export const RContributingSegmentsOptionsRowDirty = t.type({
  [ContributingSegmentsOptionsEnum.ROW]: t.union([t.number, t.string]),
});
export const RContributingSegmentsOptionsRowAndSubMetricDirty = t.intersection([
  RContributingSegmentsOptionsSubMetricDirty,
  RContributingSegmentsOptionsRowDirty,
]);

export const RContributingSegmentsOptionsDirty = t.union([
  RContributingSegmentsOptionsBooleanDirty,
  RContributingSegmentsOptionsSubMetricDirty,
  RContributingSegmentsOptionsRowDirty,
  RContributingSegmentsOptionsRowAndSubMetricDirty,
]);

/*
 * Type.
 */

export interface AnalyticsDetailsConversation extends t.TypeOf<typeof RAnalyticsDetailsConversationDirty> {}
export interface AnalyticsDetailsConversations extends t.TypeOf<typeof RAnalyticsDetailsConversationsDirty> {}

export type ContributingSegmentsOptions = t.TypeOf<typeof RContributingSegmentsOptionsDirty>;

export type ReportFilterOptionsWithContributingSegmentOptions = AnalyticsReportFilterOptions &
  Required<Pick<AnalyticsReportFilterOptions, ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS>>;

/*
 * IO methods.
 */

function parseAnalyticsDetailsConversations(src: any): AnalyticsDetailsConversations {
  return {
    items: src.conversations.map((conv: any) => ({
      // parseConversation doesn't keep the full tags.
      // Instead of getting them from the store based on their ids (and potentially failing if they're not present),
      // we keep the full objects to display them in the UI.
      ...parseConversation(conv),
      tags: parseTags(conv),
    })),
    nonAccessibleConversationIds: src.nonAccessibleConversationIds,
    metadata: src.metadata,
  };
}

/*
 * API type.
 */

// Represents a list of conversations. This is what we display in the Metric Details panel.
export const AnalyticsDetailsConversationsApi = makeApiType(
  'analyticsDetailsConversations',
  RAnalyticsDetailsConversationsDirty,
  parseAnalyticsDetailsConversations,
);

/*
 * Type guards.
 */

export const isContributingSegmentsOptionsWithSubMetric = RContributingSegmentsOptionsSubMetricDirty.is;
